/**
 * Shared presentation of a visit's insurance-authorization coverage.
 *
 * The backend derives the status (see `util/visit-authorization.ts`) and sends
 * it on the scheduled visit, on a clinical form's visit summary, and on QA
 * Center rows. Everything here is display only, so the calendar, Eval & Orders
 * and QA Center say the same thing in the same red.
 *
 * An uncovered visit is never blocked — it is scheduled and worked as normal.
 * The colour exists so the office notices it before it becomes a denied claim.
 */

/**
 * An unauthorized row is recoloured, never resized or reweighted — the red is
 * the whole signal, so the table keeps its rhythm. These are colour-only
 * classes for that reason: apply them alongside the cell's existing size and
 * weight rather than in place of them.
 */
export const UNAUTHORIZED_TEXT = "text-red-600";
/** For secondary text (MRN, roles, em-dashes) that must stay subordinate. */
export const UNAUTHORIZED_TEXT_MUTED = "text-red-400";
/** Chip treatment on the coloured calendar event. */
export const UNAUTHORIZED_RING = "ring-2 ring-red-600 ring-offset-1";
/** Legend + summary swatch. */
export const UNAUTHORIZED_HEX = "#dc2626";

const REASON_LABELS = {
  covered: "Authorized",
  no_authorization: "No authorization for this discipline",
  out_of_range: "Visit date is outside the authorization period",
  limit_exceeded: "Authorized visits already used up",
  unknown_discipline: "Discipline does not map to an authorization",
};

export const UNAUTHORIZED_LABEL = "Unauthorized";

/**
 * True when the visit should be shown in red. A missing status means the caller
 * has no visit to check (e.g. a physician order), which is not a problem.
 */
export const isVisitUnauthorized = (authorization) =>
  Boolean(authorization) && authorization.authorized === false;

/** Short reason for a badge; full sentence for a tooltip. */
export const visitAuthorizationReasonLabel = (authorization) =>
  REASON_LABELS[authorization?.reason] || UNAUTHORIZED_LABEL;

/**
 * Tooltip text. Authorized visits report which authorization paid for them and
 * where they sit in its allowance, so "3 of 5" is visible without opening the
 * Auth & Schedule tab.
 */
export const visitAuthorizationTitle = (authorization) => {
  if (!authorization) return "";
  if (authorization.authorized) {
    const auth = authorization.authNumber
      ? `Auth ${authorization.authNumber}`
      : "Authorized";
    return authorization.sequence && authorization.authorizedUnits
      ? `${auth} · visit ${authorization.sequence} of ${authorization.authorizedUnits}`
      : auth;
  }
  return `${UNAUTHORIZED_LABEL} — ${visitAuthorizationReasonLabel(authorization)}`;
};
